import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import { Box, Button, LinearProgress, SnackbarContent } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ModalPasswordReset from '../components/PasswordRest/ModalPasswordReset';
import PndRestApi from '../../../../../utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import PageHeader from 'src/shared/components/layout/PageHeader';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';

const RiderPasswordResetIndex = () => {
  const [reactTableData, setReactTableData] = useState({ tableRowsData: [] });
  const [selectedRider, setSelectedRider] = useState(null);
  const [temp, setTempData] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPasswordDiv, setPasswordDiv] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);

  useEffect(() => {
    getAllRidersDetails();
  }, []);

  const getAllRidersDetails = async () => {
    try {
      setLoading(true);
      setNoRecordFound(false);
      const response = await PndRestApi.GetAllRidersDetails();
      if (response.status === 200 && response.data.status === 200) {
        const list = response.data?.data || [];
        setReactTableData({ tableRowsData: list });
        if (list.length > 0) {
          setSelectedRider(list[0]);
        } else {
          setSelectedRider(null);
        }
      } else if (response.status === 200 && response.data.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableRowsData: [] });
        setSelectedRider(null);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.status}`,
          `Message:${response.data?.message}`,
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = (rider) => {
    if (rider) {
      setTempData(rider);
      setPasswordDiv(true);
    }
  };

  const RIDER_COLUMNS = useMemo(() => [
    {
      key: 'riderName',
      header: 'Rider Name',
      sortable: true,
    },
    {
      key: 'userName',
      header: 'Rider Unique Name',
      sortable: true,
    }
  ], []);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <PageHeader
          icon={<LockIcon fontSize="large" />}
          title="Rider Password Reset"
          subtitle="Manage credentials and passwords for active delivery riders."
          gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
        />

        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

        <Row style={{ marginTop: '20px' }}>
          {/* Left Panel: Master List */}
          <Col md={7} lg={7}>
            {!isNoRecordFound ? (
              <RadioSelectTable
                rows={reactTableData.tableRowsData || []}
                columns={RIDER_COLUMNS}
                rowKey="riderId"
                selectedKey={selectedRider?.riderId}
                onSelect={setSelectedRider}
                searchKeys={['riderName', 'userName']}
                searchPlaceholder="Search by Rider name or username..."
                pageSizes={[5, 10, 20]}
              />
            ) : (
              <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
                <p className="text-muted m-0">No rider records found.</p>
              </Card>
            )}
          </Col>

          {/* Right Panel: Detail Card & Actions */}
          <Col md={5} lg={5}>
            <Card className="card--not-full-height" style={{
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
              minHeight: '400px',
              background: '#ffffff',
              overflow: 'hidden',
            }}>
              {selectedRider ? (
                <>
                  {/* Header Banner */}
                  <div style={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    padding: '24px 20px',
                    color: '#ffffff'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <AccountCircleIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedRider.riderName}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          Username: {selectedRider.userName}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Details Body */}
                  <CardBody style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>

                      {/* General Info */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          Rider Account Profile
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Full Name:</span>
                            <span className="fw-bold small text-dark">{selectedRider.riderName || '—'}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Unique Username:</span>
                            <span className="fw-bold small text-dark">{selectedRider.userName || '—'}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Rider ID:</span>
                            <span className="fw-bold small text-dark">{selectedRider.riderId || '—'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Button Section */}
                      <div className="mt-auto pt-3">
                        <Button
                          variant="contained"
                          color="error"
                          fullWidth
                          startIcon={<LockIcon />}
                          onClick={() => resetPassword(selectedRider)}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: '10px',
                            backgroundColor: '#ef4444',
                            '&:hover': { backgroundColor: '#dc2626' }
                          }}
                        >
                          Reset Rider Password
                        </Button>
                      </div>

                    </div>
                  </CardBody>
                </>
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '400px',
                }}>
                  <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>👤</div>
                  <h5 className="fw-bold text-muted">No Rider Selected</h5>
                  <p className="text-muted small">Select a rider from the list to manage their password.</p>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {isNoRecordFound && <SnackbarContent message="No Record Found" sx={{ mt: 2 }} />}

      {isPasswordDiv && (
        <ModalPasswordReset
          modalShow={isPasswordDiv}
          setModalShow={setPasswordDiv}
          userData={temp}
        />
      )}
    </Box>
  );
};

export default RiderPasswordResetIndex;